/**
 * Clipboard integration for the `@tiptap/markdown` extension.
 *
 * The official markdown extension provides parsing/serialization
 * (`editor.getMarkdown()`, `contentType: "markdown"`) but — unlike the old
 * `tiptap-markdown` plugin — ships no clipboard wiring. This extension
 * restores both directions:
 *
 *  - **Copy** (`clipboardTextSerializer`): the copied slice is serialized
 *    through the editor's own MarkdownManager, so the `text/plain` clipboard
 *    flavor carries markdown source (`**bold**`, `- lists`, `$math$`, …)
 *    instead of ProseMirror's stripped plain text. The `text/html` flavor is
 *    untouched, so pasting into rich-text targets is unchanged.
 *  - **Paste** (`clipboardTextParser`): plain text pasted into the editor is
 *    parsed as markdown, so pasting `# Title` produces a heading. A
 *    plain-text paste (Ctrl+Shift+V) bypasses the transform, matching the
 *    old plugin's behavior.
 *
 * Requires the `Markdown` extension to be registered on the same editor —
 * without it (`editor.markdown` undefined) both hooks fall through to
 * ProseMirror's defaults.
 */
import { Extension } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// ProseMirror's `someProp` treats a null return as "fall through to the
// default behavior", but the DirectEditorProps types don't admit null. This
// cast keeps that (supported, documented) escape hatch type-checkable.
const FALL_THROUGH = null as never;

export const MarkdownClipboard = Extension.create({
  name: "markdownClipboard",

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: new PluginKey("markdownClipboard"),
        props: {
          clipboardTextParser(text, _context, plainText) {
            // `plainText` is true for an explicit plain-text paste
            // (Ctrl+Shift+V) — honor it by skipping the markdown parse.
            if (plainText) return FALL_THROUGH;
            const manager = editor.markdown;
            if (!manager) return FALL_THROUGH;
            const json = manager.parse(text);
            const content = json.content ?? [];
            if (content.length === 0) return FALL_THROUGH;
            const nodes = content.map((node) =>
              editor.schema.nodeFromJSON(node),
            );
            // `maxOpen` cuts the slice open at both ends so a single
            // paragraph pastes inline and multi-block content merges with
            // the surrounding blocks the way a normal paste does.
            return Slice.maxOpen(Fragment.from(nodes));
          },
          clipboardTextSerializer(slice) {
            const manager = editor.markdown;
            if (!manager) return FALL_THROUGH;
            const content = slice.content.toJSON() ?? [];
            // Wrap in a doc node: the serializer only inserts the blank-line
            // separator between top-level blocks when their parent is a doc.
            // A bare node array serializes each block correctly but joins
            // them with nothing ("---# Heading## Next…").
            return manager.serialize({ type: "doc", content });
          },
        },
      }),
    ];
  },
});
